import { motion } from 'framer-motion';
import { useState } from 'react';
import { Mail, Phone, MapPin, Send, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

import { API_URL } from '../config/api';
const API = `${API_URL}/api`;
const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

export default function ContactPage() {
  return (
    <>
      <SEO page="contact" />
      <ContactPageContent />
    </>
  );
}

function ContactPageContent() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succès ! Nous te répondrons rapidement.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
              Contacte-nous
            </h1>
            <p className="text-lg md:text-xl text-rm-text-secondary">
              Une question ? Un projet ? N'hésitez pas à nous contacter. 
              Nous te répondrons dans les plus brefs délais.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text mb-6">
                Envoyez-nous un message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-2">
                      Nom *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ton nom"
                      className="rounded-xl"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ton@email.com"
                      className="rounded-xl"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-2">
                      Téléphone
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 XX XX XX XX"
                      className="rounded-xl"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-2">
                      Entreprise
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nom de ton entreprise"
                      className="rounded-xl"
                      data-testid="contact-company"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Comment pouvons-nous t'aider ?"
                    rows={5}
                    className="rounded-xl"
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full py-6 text-lg"
                  data-testid="contact-submit"
                >
                  {isLoading ? 'Envoi en cours...' : 'Envoyer le message'}
                  <Send className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-rm-text mb-6">
                  Ou réserve directement un entretien
                </h2>
                <p className="text-rm-text-secondary mb-6">
                  Préférez un échange direct ? Réserve un créneau avec Romain 
                  pour discuter de ton projet et de tes besoins.
                </p>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-rm-primary hover:bg-rm-primary/90 text-white font-semibold rounded-full px-8 py-6 text-lg group">
                    Réserver un entretien
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>

              <div className="space-y-6 pt-8 border-t border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rm-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-rm-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rm-text mb-1">Email</h3>
                    <p className="text-rm-text-secondary">hello@recrutermalin.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rm-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-rm-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rm-text mb-1">Téléphone</h3>
                    <p className="text-rm-text-secondary">Réserve un entretien via Calendly</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rm-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-rm-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rm-text mb-1">Localisation</h3>
                    <p className="text-rm-text-secondary">Europe (France, Suisse) & Madagascar</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
